import React, { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormRow } from '@gravity-ui/components';
import { Button, Link, TextInput } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';

import { useNavigateReplace, useSafeParams } from 'hooks';
import { useUserCheckTokenQuery, useUserResetPassMutation } from 'store/api';
import { routes } from 'routes';

import { EyeButton } from '../../ui-kit';
import { ErrorMessage } from '../../utils';
import {
	ADMINISTRATOR,
	CONFIRMATION_PASSWORD,
	EMAIL,
	NEW_PASSWORD,
	PASSWORD_PROBLEM,
	PASSWORD_RECOVERY,
	SAVE_NEW_PASSWORD,
	SUCCESS_RESET_PASS,
} from '../constants';
import type { ResetPass } from '../utils';
import { useValidationResetPass } from '../utils';

import styles from './styles.module.scss';
import { PASSWORD_MESSAGE } from 'features/registration/constants';

export const LoginResetPass = () => {
	const [hidePass, setHidePass] = useState<boolean>(true);
	const navigate = useNavigateReplace();
	const { token } = useSafeParams();

	const { data: response } = useUserCheckTokenQuery(token ? { token } : skipToken, {
		skip: !token,
	});

	const data = useMemo(() => {
		return response;
	}, [response]);

	const inputType = hidePass ? 'password' : undefined;

	const { resetPassSchema: schema } = useValidationResetPass();

	const methods = useForm<ResetPass>({
		resolver: zodResolver(schema),
		defaultValues: { password: '', confirm: '', token: token },
	});

	const [userResetPass, { isLoading, isError, error, isSuccess }] = useUserResetPassMutation();

	const {
		watch,
		register,
		handleSubmit,
		formState: { errors },
	} = methods;

	const formValues = watch();

	useEffect(() => {
		if (isSuccess) {
			toast.success(SUCCESS_RESET_PASS, { position: 'top-right' });
			navigate(routes.login.path);
		}
		if (isError) {
			toast.error((error as any).data.message, {
				position: 'top-right',
			});
			navigate(routes.login.path);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	const onSubmitHandler: SubmitHandler<ResetPass> = (values) => {
		userResetPass(values);
	};

	return (
		<div className={styles.reset}>
			{!isLoading && data && (
				<>
					<h1>{PASSWORD_RECOVERY}</h1>

					<FormProvider {...methods}>
						<form className={styles.reset__form}>
							<div className={styles.reset__form__group}>
								<FormRow label={NEW_PASSWORD} direction="column">
									<TextInput
										size="xl"
										{...register('password')}
										value={formValues.password}
										error={errors.password?.message || isError}
										note={<span>{PASSWORD_MESSAGE}</span>}
										type={inputType}
										endContent={
											<EyeButton opened={hidePass} onClick={() => setHidePass(!hidePass)} />
										}
									/>
								</FormRow>
							</div>

							<div className={styles.reset__form__group}>
								<FormRow label={CONFIRMATION_PASSWORD} direction="column">
									<TextInput
										size="xl"
										{...register('confirm')}
										value={formValues.confirm}
										error={errors.confirm?.message || isError}
										type={inputType}
										endContent={
											<EyeButton opened={hidePass} onClick={() => setHidePass(!hidePass)} />
										}
									/>
								</FormRow>
							</div>

							<Button
								onClick={handleSubmit(onSubmitHandler)}
								loading={isLoading}
								type="submit"
								size="xl"
								className={styles.reset__form__btn}
							>
								{SAVE_NEW_PASSWORD}
							</Button>
						</form>
					</FormProvider>

					<div className={styles.reset__help}>
						<span className={styles.reset__help__info}>{PASSWORD_PROBLEM}</span>
						<span className={styles.reset__help__info}>
							{ADMINISTRATOR}{' '}
							<Link view="normal" href="mailto:edu@fond-igra.ru">
								{EMAIL}
							</Link>
						</span>
					</div>
				</>
			)}

			{!isLoading && !data && <ErrorMessage />}
		</div>
	);
};
